import { Injectable } from '@angular/core'
import { CookieService } from 'ngx-cookie-service'
import { delay } from 'rxjs/operators'
import { LoggedInUser, ParseService } from '../../parse.service'

declare global {
    interface Window {
        fbq?: {
            getState?(): { pixels?: [{ id?: string }] }
            (action: 'track', ev: string, opts: unknown, opts2: { eventID: string }): void
            (action: 'init', pixelID: string, opts: { external_id: string }): void
        }
    }
}

@Injectable({
    providedIn: 'root',
})
export class FacebookPixelService {
    private eventCounter = 0

    constructor(
        private parseService: ParseService,
        private cookieService: CookieService
    ) {
        const subscription = this.parseService.user
            // ensures subscription has a value before unsubscribing
            .pipe(delay(0))
            .subscribe((user) => {
                try {
                    if (user) {
                        this.initUser(user)
                        this.trackPageView().catch((err) => {})

                        subscription.unsubscribe()
                    }
                } catch (err) {
                    this.parseService.reportUIError(err, 'FacebookPixelService.constructor', JSON.stringify({ userID: user?.id }))
                }
            })
    }

    public async trackAddToCart(val: number) {
        try {
            if (document.location.host !== 'app.wisepelican.com') {
                return
            }

            if (val <= 0) {
                return
            }

            const value = +val.toFixed(2)
            const user = this.parseService.user.value ?? { id: '' }
            const eventID = `${user.id}-${this.eventCounter++}-${Date.now()}`
            const eventSourceID = window.location.href
            const fbc = this.getFbc()
            const fbp = this.getFbp()

            this.track('AddToCart', eventID, { currency: 'USD', value })

            await this.parseService.sendFacebookPixelAddToCartEvent(eventID, eventSourceID, fbc, fbp, value)
        } catch (err) {
            this.parseService.reportUIError(err, 'FacebookPixelService.trackAddToCart', JSON.stringify({}))
        }
    }

    private initUser(user: LoggedInUser) {
        try {
            if (document.location.host !== 'app.wisepelican.com') {
                return
            }

            const pixels = window.fbq?.getState?.().pixels ?? []
            if (window.fbq) {
                for (const pixel of pixels) {
                    if (pixel.id) {
                        window.fbq('init', pixel.id, { external_id: user.id })
                    }
                }
            }
        } catch (err) {
            this.parseService.reportUIError(err, 'FacebookPixelService.initUser', JSON.stringify({ userID: user.id }))
        }
    }

    public async trackCompleteRegistration() {
        try {
            if (document.location.host !== 'app.wisepelican.com') {
                return
            }

            const user = this.parseService.user.value ?? { id: '' }
            const eventID = `${user.id}-${this.eventCounter++}-${Date.now()}`
            const eventSourceID = window.location.href
            const fbc = this.getFbc()
            const fbp = this.getFbp()

            this.track('CompleteRegistration', eventID, { external_id: user.id })

            await this.parseService.sendFacebookPixelCompleteRegistrationEvent(eventID, eventSourceID, fbc, fbp)
        } catch (err) {
            this.parseService.reportUIError(err, 'FacebookPixelService.trackCompleteRegistration', JSON.stringify({}))
        }
    }

    public async trackInitiateCheckout(val: number) {
        try {
            if (document.location.host !== 'app.wisepelican.com') {
                return
            }

            if (val <= 0) {
                return
            }

            const value = +val.toFixed(2)
            const user = this.parseService.user.value ?? { id: '' }
            const eventID = `${user.id}-${this.eventCounter++}-${Date.now()}`
            const eventSourceID = window.location.href
            const fbc = this.getFbc()
            const fbp = this.getFbp()

            this.track('InitiateCheckout', eventID, { currency: 'USD', value })

            await this.parseService.sendFacebookPixelInitiateCheckoutEvent(eventID, eventSourceID, fbc, fbp, value)
        } catch (err) {
            this.parseService.reportUIError(err, 'FacebookPixelService.trackInitiateCheckout', JSON.stringify({ val }))
        }
    }

    private async trackPageView() {
        try {
            if (document.location.host !== 'app.wisepelican.com') {
                return
            }

            const user = this.parseService.user.value ?? { id: '' }
            const eventID = `${user.id}-${this.eventCounter++}-${Date.now()}`
            const eventSourceID = window.location.href
            const fbc = this.getFbc()
            const fbp = this.getFbp()

            this.track('PageView', eventID, undefined)

            await this.parseService.sendFacebookPixelPageViewEvent(eventID, eventSourceID, fbc, fbp)
        } catch (err) {
            this.parseService.reportUIError(err, 'FacebookPixelService.trackPageView', JSON.stringify({}))
        }
    }

    public async trackPurchase(val: number) {
        try {
            if (document.location.host !== 'app.wisepelican.com') {
                return
            }

            if (val <= 0) {
                return
            }

            const value = +val.toFixed(2)
            const user = this.parseService.user.value ?? { id: '' }
            const eventID = `${user.id}-${this.eventCounter++}-${Date.now()}`
            const eventSourceID = window.location.href
            const fbc = this.getFbc()
            const fbp = this.getFbp()

            this.track('Purchase', eventID, { currency: 'USD', value })
            await this.parseService.sendFacebookPixelPurchaseEvent(eventID, eventSourceID, fbc, fbp, value)
        } catch (err) {
            this.parseService.reportUIError(err, 'FacebookPixelService.trackPurchase', JSON.stringify({ val }))
        }
    }

    public async trackViewContent() {
        try {
            if (document.location.host !== 'app.wisepelican.com') {
                return
            }

            const user = this.parseService.user.value ?? { id: '' }
            const eventID = `${user.id}-${this.eventCounter++}-${Date.now()}`
            const eventSourceID = window.location.href
            const fbc = this.getFbc()
            const fbp = this.getFbp()

            this.track('ViewContent', eventID, undefined)
            await this.parseService.sendFacebookPixelViewContentEvent(eventID, eventSourceID, fbc, fbp)
        } catch (err) {
            this.parseService.reportUIError(err, 'FacebookPixelService.trackViewContent', JSON.stringify({}))
        }
    }

    private track(ev: 'AddToCart' | 'InitiateCheckout' | 'Purchase', eventID: string, opts: { currency: 'USD'; value: number }): void
    private track(ev: 'CompleteRegistration', eventID: string, opts: { external_id: string }): void
    private track(ev: 'PageView' | 'ViewContent', eventID: string, opts: undefined): void
    private track(ev: string, eventID: string, opts: { currency?: 'USD'; value?: number; external_id?: string } | undefined) {
        try {
            if (document.location.host !== 'app.wisepelican.com') {
                return
            }

            if (window.fbq) {
                window.fbq('track', ev, opts ?? {}, { eventID })
            }
        } catch (err) {}
    }

    private getFbp() {
        try {
            const fbp = this.cookieService.get('_fbp')
            return fbp
        } catch (err) {
            this.parseService.reportUIError(err, 'FacebookPixelService.getFbp', JSON.stringify({}))
        }
    }

    private getFbc() {
        try {
            const fbc = this.cookieService.get('_fbc')
            return fbc
        } catch (err) {
            this.parseService.reportUIError(err, 'FacebookPixelService.getFbc', JSON.stringify({}))
        }
    }
}
